import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import {
  Grid,
  MenuItem,
  Select,
  Typography,
  makeStyles,
  RadioGroup,
  FormControlLabel,
  Radio,
  Box,
} from '@material-ui/core';
import moment from 'moment-timezone';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';

import { DatePickers, TimezoneSelect } from 'components';
import { ContentEmailValues } from 'features/admin/types';
import {
  NO_SECONDS,
  DAILY_SECONDS,
  WEEKLY_SECONDS,
  MONTHLY_SECONDS,
} from 'features/admin/constants';

import Tip from 'components/Tip';

enum PilotValues {
  Auto = 'auto',
  Custom = 'custom',
}

const useStyles = makeStyles(theme => ({
  spacer: {
    marginTop: theme.spacing(3),
  },
  error: {
    height: theme.spacing(),
    paddingTop: theme.spacing(),
  },
  switch: {
    position: 'relative',
    top: -theme.spacing(),
  },
  deliveryTime: {
    paddingTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
}));

const When: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { values, errors, setFieldValue } = useFormikContext<ContentEmailValues>();
  const { delivery_interval, send_at, use_sto } = values;

  const [radioValue, setRadioValue] = useState(use_sto ? PilotValues.Auto : PilotValues.Custom);
  const [dateOnly, setDateOnly] = useState(send_at);
  const [timeOnly, setTimeOnly] = useState(send_at);
  const [tz, setTz] = useState(moment.tz.guess());

  function handleChange(nextDateOnly: number, nextTimeOnly: number, nextTz: string) {
    const nextTime = moment.unix(nextTimeOnly);
    const nextDate = moment.unix(nextDateOnly);
    const nextSendAt = moment()
      .tz(nextTz)
      .year(nextDate.year())
      .month(nextDate.month())
      .date(nextDate.date())
      .hour(nextTime.hour())
      .minute(nextTime.minute());
    setFieldValue('send_at', nextSendAt.unix());
    setDateOnly(nextDateOnly);
    setTimeOnly(nextTimeOnly);
    setTz(nextTz);
  }

  function onChangeRadio(event: ChangeEvent<HTMLInputElement>, value: PilotValues) {
    setRadioValue(value);
    setFieldValue('use_sto', value === 'auto');
  }

  return (
    <Grid container>
      <Grid item xs={3}>
        <Typography variant="subtitle2">{t('common:when')}</Typography>
      </Grid>
      <Grid item xs={9}>
        <Grid container spacing={3}>
          <Grid item>
            <DatePickers.Select
              value={dateOnly}
              setValue={(nextDateOnly: number) => handleChange(nextDateOnly, timeOnly, tz)}
            />
          </Grid>
          <Grid item>
            <Select
              value={delivery_interval}
              onChange={event => {
                setFieldValue('delivery_interval', event.target.value);
                setFieldValue('grouping_interval', event.target.value || MONTHLY_SECONDS);
              }}
            >
              <MenuItem value={NO_SECONDS}>{t('contentEmails:repeatNone')}</MenuItem>
              <MenuItem value={DAILY_SECONDS}>{t('contentEmails:repeatDaily')}</MenuItem>
              <MenuItem value={WEEKLY_SECONDS}>{t('contentEmails:repeatWeekly')}</MenuItem>
              <MenuItem value={MONTHLY_SECONDS}>{t('contentEmails:repeatMonthly')}</MenuItem>
            </Select>
          </Grid>
        </Grid>

        <RadioGroup onChange={onChangeRadio} value={radioValue}>
          <Box marginTop={2} marginBottom={1}>
            <FormControlLabel
              label={
                <Fragment>
                  <Typography display="inline" variant="body2">
                    {t('contentEmails:autoPilotTitle')}
                  </Typography>
                  {' - '}
                  <Typography display="inline" color="textSecondary">
                    {t('contentEmails:autoPilotHelp')}
                  </Typography>
                </Fragment>
              }
              control={
                <Radio
                  color="primary"
                  icon={<CheckBoxOutlineBlankIcon />}
                  checkedIcon={<CheckBoxIcon />}
                />
              }
              value={PilotValues.Auto}
            />
          </Box>

          <Box marginTop={1} marginBottom={1}>
            <FormControlLabel
              label={
                <Fragment>
                  <Typography display="inline" variant="body2">
                    {t('contentEmails:customDeliveryTime')}
                  </Typography>
                  {' - '}
                  <Typography display="inline" color="textSecondary">
                    {t('contentEmails:customDeliveryHelp')}
                  </Typography>
                </Fragment>
              }
              control={
                <Radio
                  color="primary"
                  icon={<CheckBoxOutlineBlankIcon />}
                  checkedIcon={<CheckBoxIcon />}
                />
              }
              value={PilotValues.Custom}
            />
          </Box>
        </RadioGroup>
        {!use_sto && (
          <Fragment>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={5}>
                <DatePickers.Time
                  value={timeOnly}
                  onChange={(nextTimeOnly: number) => handleChange(dateOnly, nextTimeOnly, tz)}
                />
              </Grid>
              <Grid item xs={7}>
                <TimezoneSelect
                  defaultValue={tz}
                  onChange={(nextTz: string) => handleChange(dateOnly, timeOnly, nextTz)}
                />
              </Grid>
            </Grid>
            {!!errors.send_at && (
              <Typography paragraph className={classes.error} color="error" variant="body2">
                {errors.send_at}
              </Typography>
            )}
          </Fragment>
        )}
        <Tip i18nKey="contentEmails:whenTip" />
      </Grid>
    </Grid>
  );
};

export default When;
